import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { AuthProvider } from './components/AuthProvider';
import { MainLayout } from './components/layout';
import { ChatPage, ImagesPage, VideosPage, SettingsPage, SharedPage } from './pages';
import { DocumentsPage } from './pages/DocumentsPage';

/**
 * Login Page Component
 * Validates: Requirements 11.1, 11.2
 */
function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const login = useAuthStore((state) => state.login);
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Get reason from URL params (for session expiry messages)
  const reason = searchParams.get('reason');

  // Debug: Log when component mounts
  useEffect(() => {
    console.log('[LoginPage] Component mounted');
    console.log('[LoginPage] Initial state - isAuthenticated:', isAuthenticated, 'isLoading:', isLoading);
  }, []);
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/chat', { replace: true });
    }
  }, [isAuthenticated, navigate]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    console.log('[LoginPage] Form submitted');
    e.preventDefault();
    console.log('[LoginPage] Calling login with email:', email);
    try {
      const success = await login(email, password);
      console.log('[LoginPage] Login result:', success);
      if (success) {
        console.log('[LoginPage] Navigating to /chat');
        navigate('/chat', { replace: true });
      } else {
        console.log('[LoginPage] Login failed, staying on login page');
      }
    } catch (error) {
      console.error('[LoginPage] Unexpected error during login:', error);
    }
  };
  
  // Get localized message for session expiry reason
  const getReasonMessage = (reasonCode: string | null): string | null => {
    if (!reasonCode) return null;
    
    const messages: Record<string, string> = {
      'SESSION_EXPIRED': 'Your session has expired. Please log in again.',
      'OTHER_DEVICE_LOGIN': 'You were logged out because you logged in on another device.',
      'ADMIN_REVOKE': 'Your session was revoked by an administrator.',
    };
    
    return messages[reasonCode] || null;
  };
  
  const reasonMessage = getReasonMessage(reason);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950">
      <div className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">LW</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">LLM Wrapper Platform</h1>
          <p className="text-gray-400">Sign in to your account</p>
        </div>
        
        {/* Session expiry message */}
        {reasonMessage && (
          <div className="mb-6 p-4 bg-yellow-900/50 border border-yellow-600 rounded-lg">
            <p className="text-yellow-200 text-sm">{reasonMessage}</p>
          </div>
        )}
        
        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/50 border border-red-600 rounded-lg">
            <p className="text-red-200 text-sm">{error}</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="you@example.com"
              disabled={isLoading}
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="••••••••"
              disabled={isLoading}
            />
          </div>
          
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-800 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-950"
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}

/**
 * Protected Route Component
 * Validates: Requirements 11.1
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

/**
 * Main App Routes
 * Validates: Requirements 20.3 - Separate navigation for text, image, and video sections
 */
function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      
      {/* Shared conversation view - public, no auth required */}
      <Route path="/shared/:shareToken" element={<SharedPage />} />
      
      {/* Protected routes with main layout */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/images" element={<ImagesPage />} />
        <Route path="/videos" element={<VideosPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
      
      {/* Redirects */}
      <Route path="/" element={<Navigate to="/chat" replace />} />
      <Route path="*" element={<Navigate to="/chat" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
